import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import {
  fileToBase64,
  EntityHeader,
  EntityBodySection,
  EntitySightingsSection,
  PlatesSection,
  EntityActionBar,
} from "@/components/entity/EntitySections";

export default function EntityDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { isAdmin } = useAuth();
  const [entity, setEntity] = useState(null);
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get(`/entities/${id}`).then((r) => setEntity(r.data)).catch(() => setEntity(false));
  }, [id]);

  const startEdit = () => { setDraft({ ...entity }); setEditing(true); };
  const cancel = () => { setEditing(false); setDraft(null); };

  const save = async () => {
    setSaving(true);
    try {
      const res = await api.put(`/entities/${id}`, draft);
      setEntity(res.data);
      setEditing(false);
      toast.success("Entity record sealed.");
    } catch {
      toast.error("Update failed.");
    } finally {
      setSaving(false);
    }
  };

  const remove = async () => {
    if (!window.confirm("Erase this entity from the catalogue?")) return;
    try {
      await api.delete(`/entities/${id}`);
      toast.success("Entity erased.");
      navigate("/entities");
    } catch {
      toast.error("Could not erase.");
    }
  };

  const onUploadImage = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 1.2 * 1024 * 1024) {
      toast.error("Image too large (max ~1.2MB).");
      return;
    }
    const b64 = await fileToBase64(file);
    setDraft((d) => ({ ...d, images: [...(d.images || []), b64] }));
  };

  const removeImage = (idx) => {
    setDraft((d) => ({ ...d, images: d.images.filter((_, i) => i !== idx) }));
  };

  if (entity === null)
    return <div className="text-[#555] text-xs tracking-widest uppercase">::: retrieving record :::</div>;
  if (entity === false)
    return (
      <div className="border border-[#333] p-12 text-center">
        <div className="font-serif-display text-3xl mb-4">Entity not catalogued.</div>
        <Link to="/entities" className="tiv-btn">Return to catalogue</Link>
      </div>
    );

  const e = editing ? draft : entity;

  // Tags in the Chronicle are stored lowercased (see TerraChronicle CaptureForm.addTag).
  // We prefer the entity's name as the tag so dispatches filed against entity-name
  // surface here; fall back to the entity id when name is missing.
  const chronicleTag = (entity.name || entity.id || "").trim().toLowerCase();

  return (
    <div className="space-y-10" data-testid="entity-detail-page">
      <div className="flex flex-wrap items-center gap-3 text-[0.65rem] tracking-[0.35em] uppercase text-[#A3A3A3]">
        <Link to="/entities" className="hover:text-[#E3E1D9]">↖ Catalogue</Link>
        <span className="text-[#555]">/</span>
        <span>{entity.id}</span>
        {chronicleTag && (
          <Link
            to={`/the-terra-chronicle?tag=${encodeURIComponent(chronicleTag)}`}
            className="ml-auto border border-[#333] px-3 py-1 hover:border-[#E3E1D9] hover:text-[#E3E1D9]"
            data-testid="entity-view-in-chronicle"
          >
            View in Chronicle →
          </Link>
        )}
      </div>

      <EntityHeader entity={entity} editing={editing} draft={draft} setDraft={setDraft} e={e} />
      <EntityBodySection editing={editing} draft={draft} entity={entity} setDraft={setDraft} />
      <EntitySightingsSection editing={editing} draft={draft} entity={entity} setDraft={setDraft} />
      <PlatesSection
        images={e.images}
        editing={editing}
        onUploadImage={onUploadImage}
        removeImage={removeImage}
        testidPrefix="entity"
      />

      {isAdmin && (
        <EntityActionBar
          editing={editing}
          saving={saving}
          onSave={save}
          onCancel={cancel}
          onEdit={startEdit}
          onRemove={remove}
        />
      )}
    </div>
  );
}
